/**
 * useFamilyMembers Hook
 *
 * Manages state for family members with CRUD operations.
 * Auto-loads family members on mount and provides refresh capability.
 * Uses scoped storage for device/user-specific data.
 */

import { useState, useEffect, useCallback } from 'react';
import { FamilyMember, CreateFamilyMemberInput, UpdateFamilyMemberInput } from '@/types/family-member';
import { useFamilyMemberManager } from '@/hooks/use-scoped-managers';

type UseFamilyMembersResult = {
  members: FamilyMember[];
  isLoading: boolean;
  error: string | null;
  meMember: FamilyMember | null;
  refresh: () => Promise<void>;
  createMember: (input: CreateFamilyMemberInput) => Promise<FamilyMember>;
  updateMember: (id: string, updates: UpdateFamilyMemberInput) => Promise<FamilyMember | null>;
  deleteMember: (id: string) => Promise<boolean>;
  getNextAvailableColor: () => Promise<string>;
};

export function useFamilyMembers(): UseFamilyMembersResult {
  const manager = useFamilyMemberManager();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!manager) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const loadedMembers = await manager.getAll();
      setMembers(loadedMembers);
    } catch (err) {
      console.error('Error loading family members:', err);
      setError(err instanceof Error ? err.message : 'Failed to load family members');
    } finally {
      setIsLoading(false);
    }
  }, [manager]);

  useEffect(() => {
    if (manager) {
      loadMembers();
    }
  }, [manager, loadMembers]);

  const meMember = members.find(m => m.isMe) || null;

  const createMember = useCallback(async (input: CreateFamilyMemberInput): Promise<FamilyMember> => {
    if (!manager) {
      throw new Error('Manager not initialized');
    }
    const newMember = await manager.create(input);
    setMembers(prev => [...prev, newMember]);
    return newMember;
  }, [manager]);

  const updateMember = useCallback(async (
    id: string,
    updates: UpdateFamilyMemberInput
  ): Promise<FamilyMember | null> => {
    if (!manager) {
      throw new Error('Manager not initialized');
    }
    const updated = await manager.update(id, updates);
    if (updated) {
      setMembers(prev => prev.map(m => m.id === id ? updated : m));
    }
    return updated;
  }, [manager]);

  const deleteMember = useCallback(async (id: string): Promise<boolean> => {
    if (!manager) {
      throw new Error('Manager not initialized');
    }
    const success = await manager.delete(id);
    if (success) {
      setMembers(prev => prev.filter(m => m.id !== id));
    }
    return success;
  }, [manager]);

  const getNextAvailableColor = useCallback(async (): Promise<string> => {
    if (!manager) {
      throw new Error('Manager not initialized');
    }
    return manager.getNextAvailableColor();
  }, [manager]);

  return {
    members,
    isLoading,
    error,
    meMember,
    refresh: loadMembers,
    createMember,
    updateMember,
    deleteMember,
    getNextAvailableColor,
  };
}
